import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Toaster } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BottomNav } from './BottomNav';
import { ControlStatusPanel } from './ControlStatusPanel';

const EDGE_SWIPE_GUARD_PX = 32;
const HORIZONTAL_LOCK_PX = 10;
const POPSTATE_BLOCK_MS = 1200;

interface B2cLayoutProps {
  children: ReactNode;
  hideNav?: boolean;
}

function usePreventEdgeSwipeBack(leftEdgeGuardRef: RefObject<HTMLDivElement>, rightEdgeGuardRef: RefObject<HTMLDivElement>) {
  const touchStartRef = useRef<{ x: number; y: number; edge: 'left' | 'right' | null } | null>(null);
  const blockPopstateUntilRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverscrollX = html.style.overscrollBehaviorX;
    const previousBodyOverscrollX = body.style.overscrollBehaviorX;
    const previousBodyOverflowX = body.style.overflowX;
    const previousBodyTouchAction = body.style.touchAction;

    html.style.overscrollBehaviorX = 'none';
    body.style.overscrollBehaviorX = 'none';
    body.style.overflowX = 'hidden';
    body.style.touchAction = 'pan-y';

    const getEdge = (x: number) => {
      const width = window.innerWidth || html.clientWidth;
      if (x <= EDGE_SWIPE_GUARD_PX) return 'left';
      if (x >= width - EDGE_SWIPE_GUARD_PX) return 'right';
      return null;
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        touchStartRef.current = null;
        return;
      }

      const touch = event.touches[0];
      const edge = getEdge(touch.clientX);
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        edge,
      };
      if (edge) blockPopstateUntilRef.current = Date.now() + POPSTATE_BLOCK_MS;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const start = touchStartRef.current;
      if (!start?.edge || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      const isHorizontalGesture = Math.abs(deltaX) > HORIZONTAL_LOCK_PX && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;
      const isBrowserBackDirection = start.edge === 'left' ? deltaX > 0 : deltaX < 0;

      if (isHorizontalGesture && isBrowserBackDirection) {
        blockPopstateUntilRef.current = Date.now() + POPSTATE_BLOCK_MS;
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      touchStartRef.current = null;
    };

    const handlePopstate = () => {
      if (Date.now() > blockPopstateUntilRef.current) return;
      window.history.go(1);
    };

    const preventEdgeGesture = (event: TouchEvent) => {
      blockPopstateUntilRef.current = Date.now() + POPSTATE_BLOCK_MS;
      event.preventDefault();
      event.stopPropagation();
    };

    const preventPointerGesture = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') return;
      blockPopstateUntilRef.current = Date.now() + POPSTATE_BLOCK_MS;
      event.preventDefault();
      event.stopPropagation();
    };

    const captureOptions: AddEventListenerOptions = { capture: true };
    const blockingOptions: AddEventListenerOptions = { capture: true, passive: false };
    const edgeGuards = [leftEdgeGuardRef.current, rightEdgeGuardRef.current].filter((node): node is HTMLDivElement => Boolean(node));

    document.addEventListener('touchstart', handleTouchStart, captureOptions);
    document.addEventListener('touchmove', handleTouchMove, blockingOptions);
    document.addEventListener('touchend', handleTouchEnd, captureOptions);
    document.addEventListener('touchcancel', handleTouchEnd, captureOptions);
    window.addEventListener('popstate', handlePopstate);
    edgeGuards.forEach((node) => {
      node.addEventListener('touchstart', preventEdgeGesture, blockingOptions);
      node.addEventListener('touchmove', preventEdgeGesture, blockingOptions);
      node.addEventListener('pointerdown', preventPointerGesture, blockingOptions);
      node.addEventListener('pointermove', preventPointerGesture, blockingOptions);
    });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, captureOptions);
      document.removeEventListener('touchmove', handleTouchMove, blockingOptions);
      document.removeEventListener('touchend', handleTouchEnd, captureOptions);
      document.removeEventListener('touchcancel', handleTouchEnd, captureOptions);
      window.removeEventListener('popstate', handlePopstate);
      edgeGuards.forEach((node) => {
        node.removeEventListener('touchstart', preventEdgeGesture, blockingOptions);
        node.removeEventListener('touchmove', preventEdgeGesture, blockingOptions);
        node.removeEventListener('pointerdown', preventPointerGesture, blockingOptions);
        node.removeEventListener('pointermove', preventPointerGesture, blockingOptions);
      });

      html.style.overscrollBehaviorX = previousHtmlOverscrollX;
      body.style.overscrollBehaviorX = previousBodyOverscrollX;
      body.style.overflowX = previousBodyOverflowX;
      body.style.touchAction = previousBodyTouchAction;
    };
  }, [leftEdgeGuardRef, rightEdgeGuardRef]);
}

export function B2cLayout({ children, hideNav }: B2cLayoutProps) {
  const leftEdgeGuardRef = useRef<HTMLDivElement>(null);
  const rightEdgeGuardRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  usePreventEdgeSwipeBack(leftEdgeGuardRef, rightEdgeGuardRef);

  return (
    <div
      className="mx-auto min-h-[100dvh] max-w-lg overflow-x-hidden overscroll-x-none bg-neutral-50 dark:bg-neutral-950"
      style={{ touchAction: 'pan-y' }}
    >
      {/* 라우트 전환 시 부드러운 진입 애니메이션 */}
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="min-h-[100dvh]"
        style={hideNav ? undefined : { paddingBottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
      >
        {children}
      </motion.main>
      <div
        ref={leftEdgeGuardRef}
        aria-hidden="true"
        className="fixed inset-y-0 left-0 z-[2147483647] w-6 touch-none select-none bg-transparent"
        style={{ overscrollBehavior: 'none', WebkitUserSelect: 'none' }}
      />
      <div
        ref={rightEdgeGuardRef}
        aria-hidden="true"
        className="fixed inset-y-0 right-0 z-[2147483647] w-6 touch-none select-none bg-transparent"
        style={{ overscrollBehavior: 'none', WebkitUserSelect: 'none' }}
      />
      {!hideNav && <BottomNav />}
      <ControlStatusPanel />
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 2500,
          style: { fontSize: '14px', marginTop: 'env(safe-area-inset-top)' },
        }}
      />
    </div>
  );
}
